import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { 
  Film, Image, Video, FileText, MapPin, Calendar, Settings, 
  LogOut, Menu, X, ChevronRight, Home, LayoutGrid, Globe
} from 'lucide-react';
import AdminLogin from './AdminLogin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    const expiry = localStorage.getItem('admin_token_expiry');

    if (!token || !expiry || Date.now() > parseInt(expiry)) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_token_expiry');
      setIsAuthenticated(false);
      setChecking(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_token_expiry');
        setIsAuthenticated(false);
      }
    } catch (err) {
      setIsAuthenticated(false);
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_token_expiry');
    setIsAuthenticated(false);
  };

  const navItems = [
    { to: '/admin', icon: Home, label: 'Dashboard', end: true },
    { to: '/admin/pages', icon: LayoutGrid, label: 'Paginas (CMS)' },
    { to: '/admin/site-config', icon: Globe, label: 'Config. Sitio / Home' },
    { to: '/admin/movie', icon: Film, label: 'Pelicula' },
    { to: '/admin/media', icon: Image, label: 'Media (Poster/Imagenes)' },
    { to: '/admin/trailer', icon: Video, label: 'Trailer' },
    { to: '/admin/cinemas', icon: MapPin, label: 'Cines' },
    { to: '/admin/sessions', icon: Calendar, label: 'Sesiones' },
    { to: '/admin/settings', icon: Settings, label: 'Configuracion' },
  ];

  if (checking) {
    return (
      <div className="min-h-screen bg-[#1a2b3c] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#50c878] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full bg-[#1a1a1a] text-white transition-all duration-300 z-50 ${
          sidebarOpen ? 'w-64' : 'w-16'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          {sidebarOpen && (
            <h1 className="text-xl font-bold text-[#50c878]">Admin Panel</h1>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-2 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-3 rounded-lg transition-all
                ${isActive 
                  ? 'bg-[#50c878] text-white' 
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
                }
              `}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="text-sm">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Bottom Actions */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 space-y-2">
          <a
            href="/"
            className="flex items-center gap-3 px-3 py-3 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-all"
          >
            <Home className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm">Ver Sitio</span>}
          </a>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-3 rounded-lg text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all w-full"
            data-testid="admin-logout"
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm">Cerrar Sesión</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-16'}`}>
        {/* Top Bar */}
        <header className="h-16 bg-white shadow-sm flex items-center justify-between px-6">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Admin</span>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900">Panel de Control</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">Administrador</span>
            <div className="w-8 h-8 rounded-full bg-[#50c878] flex items-center justify-center text-white text-sm font-medium">
              A
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
